import { supabase } from './supabase';

// All storage operations use the shared supabase client from supabase.js
// This ensures auth tokens are shared between Auth, DB, and Storage.

const BUCKET = 'property-photos';

// ── UPLOAD ─────────────────────────────────────────────────────
export async function uploadMediaFile(file, customerId, staffName, visitDate) {
  const safeName  = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const timestamp = Date.now();
  const path      = `${customerId}/${visitDate}/${timestamp}-${safeName}`;

  const { data: uploadData, error: uploadError } = await supabase
    .storage.from(BUCKET).upload(path, file, { cacheControl: '3600', upsert: false });

  if (uploadError) return { record: null, error: uploadError };

  const fileType = file.type.startsWith('video') ? 'video' : 'image';

  const { data: record, error: dbError } = await supabase
    .from('customer_media')
    .insert({
      customer_id:  customerId,
      storage_path: uploadData.path,
      file_type:    fileType,
      file_name:    file.name,
      file_size:    file.size,
      status:       'pending',
      uploaded_by:  staffName,
      visit_date:   visitDate,
      created_at:   new Date().toISOString(),
    })
    .select()
    .single();

  if (dbError) {
    await supabase.storage.from(BUCKET).remove([uploadData.path]);
    return { record: null, error: dbError };
  }

  return { record, error: null };
}

export async function uploadMultipleFiles(files, customerId, staffName, visitDate, onProgress) {
  const results = [];
  let completed = 0;
  for (const file of files) {
    const result = await uploadMediaFile(file, customerId, staffName, visitDate);
    results.push({ ...result, file });
    completed++;
    if (onProgress) onProgress(Math.round((completed / files.length) * 100));
  }
  return results;
}

// ── FETCH ──────────────────────────────────────────────────────
export async function getCustomerMediaWithUrls(customerId, status = 'approved') {
  let query = supabase
    .from('customer_media')
    .select('*')
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false });

  if (status !== 'all') query = query.eq('status', status);

  const { data: records, error } = await query;
  if (error || !records) return { media: [], error };

  const media = await Promise.all(
    records.map(async (record) => {
      const { data: urlData } = await supabase
        .storage.from(BUCKET).createSignedUrl(record.storage_path, 3600);
      return { ...record, url: urlData?.signedUrl || null };
    })
  );
  return { media, error: null };
}

export async function getAllPendingMedia() {
  const { data: records, error } = await supabase
    .from('customer_media')
    .select('*, customers ( id, name, address )')
    .eq('status', 'pending')
    .order('created_at', { ascending: false });

  if (error || !records) return { media: [], error };

  const media = await Promise.all(
    records.map(async (record) => {
      const { data: urlData } = await supabase
        .storage.from(BUCKET).createSignedUrl(record.storage_path, 3600);
      return { ...record, url: urlData?.signedUrl || null };
    })
  );
  return { media, error: null };
}

export async function getAllApprovedMedia() {
  const { data: records, error } = await supabase
    .from('customer_media')
    .select('*, customers ( id, name, address )')
    .eq('status', 'approved')
    .order('approved_at', { ascending: false });

  if (error || !records) return { media: [], error };

  const media = await Promise.all(
    records.map(async (record) => {
      const { data: urlData } = await supabase
        .storage.from(BUCKET).createSignedUrl(record.storage_path, 3600);
      return { ...record, url: urlData?.signedUrl || null };
    })
  );
  return { media, error: null };
}

// ── APPROVE ────────────────────────────────────────────────────
export async function approveMediaRecord(id) {
  const { error } = await supabase
    .from('customer_media')
    .update({ status: 'approved', approved_at: new Date().toISOString() })
    .eq('id', id);
  return { error };
}

export async function approveAllPendingForCustomer(customerId) {
  const { error } = await supabase
    .from('customer_media')
    .update({ status: 'approved', approved_at: new Date().toISOString() })
    .eq('customer_id', customerId)
    .eq('status', 'pending');
  return { error };
}

// ── DELETE ─────────────────────────────────────────────────────
export async function deleteMediaRecord(id, storagePath) {
  const { error: dbError } = await supabase
    .from('customer_media').delete().eq('id', id);
  if (dbError) return { error: dbError };
  await supabase.storage.from(BUCKET).remove([storagePath]);
  return { error: null };
}

// ── VALIDATION ─────────────────────────────────────────────────
export function validateFileSize(file) {
  const MAX_IMAGE = 10  * 1024 * 1024;
  const MAX_VIDEO = 100 * 1024 * 1024;
  const isVideo   = file.type.startsWith('video');
  const limit     = isVideo ? MAX_VIDEO : MAX_IMAGE;
  if (file.size > limit) {
    return { valid: false, error: `${file.name} is too large. ${isVideo ? 'Videos' : 'Images'} must be under ${isVideo ? '100MB' : '10MB'}.` };
  }
  return { valid: true, error: null };
}

export function formatFileSize(bytes) {
  if (bytes < 1024)         return `${bytes} B`;
  if (bytes < 1024 * 1024)  return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function queueForRetry(fileInfo) {
  try {
    const queue = JSON.parse(localStorage.getItem('media_retry_queue') || '[]');
    queue.push({ ...fileInfo, queuedAt: new Date().toISOString() });
    localStorage.setItem('media_retry_queue', JSON.stringify(queue));
  } catch { /* ignore */ }
}
